import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { staffKycService, staffMandateService } from "../../services/api";
import toast from "react-hot-toast";
import { logo } from "../../constants/icons";

// TP-Coach-Mandate — mirror of PlayerMandateKycStart. Differences vs the
// player template (documented, Brief §4.5 / D-006):
//  - identity key is the TheSports coach id (ts_coach_id), not player_id;
//  - the coach chain is pure biometric from the start — there is no legacy
//    DocuPass verification-link leg, so "Start Verification" calls the
//    biometric start endpoint directly and hands its payload (reference
//    photo = coach photo) to the biometric page.
const StaffMandateKycStart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token: paramToken } = useParams();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const queryToken = searchParams.get("invite_token");
  const queryTsCoachId =
    searchParams.get("ts_coach_id") || searchParams.get("coach_id");
  const queryMobile = searchParams.get("mobile");

  const {
    tsCoachId: stateTsCoachId,
    mobile: stateMobile,
    token: stateToken,
    verification: stateVerification,
    inviteParams,
    rawQuery,
    email,
    inviteSentBy,
    senderType,
    agencyId,
    type,
  } = location.state || {};

  const mandateToken = stateToken || paramToken || queryToken || null;
  const [tsCoachId, setTsCoachId] = useState(
    stateTsCoachId || queryTsCoachId || null,
  );
  const [mobile] = useState((stateMobile || queryMobile || "").trim());
  const [loading, setLoading] = useState(false);
  const [fetchingMandate, setFetchingMandate] = useState(false);
  const [verificationInfo, setVerificationInfo] = useState(
    stateVerification || null,
  );
  const [mandateError, setMandateError] = useState("");
  const [hasFetchedDetails, setHasFetchedDetails] = useState(false);

  const basePath = useMemo(() => {
    if (mandateToken) {
      return `/staff-register-mandate/${mandateToken}`;
    }
    return "/staff-register-mandate";
  }, [mandateToken]);

  const navigationState = {
    token: mandateToken,
    tsCoachId,
    mobile,
    verification: verificationInfo,
    inviteParams,
    rawQuery,
    email,
    inviteSentBy,
    senderType,
    agencyId,
    type,
  };

  useEffect(() => {
    if (!mandateToken) {
      toast.error(
        "Invalid or expired invitation. Please reopen your invite link.",
      );
      navigate("/staff-register-mandate", { replace: true });
    }
  }, [mandateToken, navigate]);

  useEffect(() => {
    setHasFetchedDetails(false);
  }, [mandateToken]);

  useEffect(() => {
    if (!mandateToken || hasFetchedDetails) {
      return;
    }

    // F8: no `mobile` trigger — the BE verification payload carries no
    // mobile number, so a fetch could never populate it anyway.
    const shouldFetchDetails = !tsCoachId || !verificationInfo;

    if (!shouldFetchDetails) {
      setHasFetchedDetails(true);
      return;
    }

    setHasFetchedDetails(true);

    const fetchMandateDetails = async () => {
      setFetchingMandate(true);
      setMandateError("");
      try {
        const response =
          await staffMandateService.getMandateDetailsByToken(mandateToken);

        if (!response.success) {
          throw new Error(
            response.error || "Failed to load invitation details",
          );
        }

        const data = response.data;

        const resolvedTsCoachId =
          data?.ts_coach_id ||
          data?.mandate?.ts_coach_id ||
          data?.coach?.ts_coach_id ||
          null;
        if (!tsCoachId && resolvedTsCoachId) {
          setTsCoachId(String(resolvedTsCoachId));
        }

        if (data?.verification) {
          setVerificationInfo(data.verification);
        }
      } catch (error) {
        console.error("Fetch staff mandate details error:", error);
        const message = error.message || "Failed to load invitation details";
        setMandateError(message);
        toast.error(message);
      } finally {
        setFetchingMandate(false);
      }
    };

    fetchMandateDetails();
  }, [mandateToken, tsCoachId, verificationInfo, hasFetchedDetails]);

  const requiresFreshVerification = useMemo(() => {
    if (!verificationInfo) {
      return true;
    }

    if (verificationInfo.reverify_required) {
      return true;
    }

    return !verificationInfo.id_verified;
  }, [verificationInfo]);

  const startVerificationFlow = async () => {
    if (loading || fetchingMandate) {
      return;
    }

    if (!mandateToken) {
      toast.error(
        "Missing representation information. Please reopen your invite link.",
      );
      return;
    }

    if (!tsCoachId) {
      toast.error(
        "Unable to identify your profile. Please reopen your invite link.",
      );
      return;
    }

    if (!requiresFreshVerification) {
      const actionPath = `${basePath}/action`;
      navigate(actionPath, {
        state: {
          ...navigationState,
          tsCoachId,
        },
      });
      return;
    }

    setLoading(true);
    try {
      // Pure-biometric chain: start directly against the coach biometric
      // endpoint (no DocuPass leg exists for coaches). The invite token is
      // passed along so the BE binds the chain to THIS invitation (F6).
      const res = await staffKycService.startBiometricVerification(
        tsCoachId,
        mandateToken
      );

      if (res?.success) {
        const biometricData = res?.data || {};
        const biometricPath = `${basePath}/biometric-verification`;
        navigate(biometricPath, {
          state: {
            ...navigationState,
            tsCoachId,
            verification: biometricData?.verification || verificationInfo,
            biometricEndpoints: biometricData,
            profilePhotoUrl: res?.profile_photo_url || biometricData?.profile_photo_url,
          },
        });
      } else {
        toast.error(
          res?.error || res?.message || "Failed to start verification"
        );
      }
    } catch (error) {
      console.error("Failed to start verification:", error);
      toast.error("Failed to start verification");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(basePath, {
      state: {
        ...navigationState,
        tsCoachId,
        mobile,
      },
    });
  };

  const buttonDisabled =
    !mandateToken || loading || fetchingMandate || !tsCoachId;

  return (
    <div className="flex min-h-screen">
      <div className="flex-1 bg-white flex items-center justify-center">
        <div className="w-full max-w-xl p-6 border border-gray-400 rounded-xl">
            <div className="flex items-center mb-6">
              <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 mr-2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5L8.25 12l7.5-7.5"
                  />
                </svg>
                Back
              </button>
            </div>

            <div className="text-center">
              <img src={logo} alt="Logo" className="mx-auto mb-4 h-10" />

              <h2 className="text-[24px] leading-[32px] font-semibold text-gray-900 mb-3">
                Identity Verification Required
              </h2>
              <p className="text-[16px] leading-[24px] text-gray-600 mb-8">
                To proceed with your representation invitation, we need to verify your
                identity for security purposes.
              </p>

              {mandateError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm font-medium">{mandateError}</p>
                  <p className="text-xs mt-1">
                    Please refresh the page or reopen the invitation email.
                  </p>
                </div>
              )}

              {fetchingMandate && (
                <div className="flex justify-center mb-6">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
              )}

              {requiresFreshVerification && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 text-left">
                  <div className="flex items-center gap-3 mb-3">
                    <svg
                      className="w-6 h-6 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <h3 className="text-lg font-medium text-blue-900">
                      Quick Face Verification
                    </h3>
                  </div>
                  <ul className="text-blue-800 space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Take a quick selfie to verify your identity
                    </li>
                    <li className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Good lighting and camera access needed
                    </li>
                    <li className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Takes less than 30 seconds
                    </li>
                  </ul>
                </div>
              )}

              {!requiresFreshVerification && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left mb-6">
                  <h3 className="text-green-800 font-medium mb-1">
                    Identity already verified
                  </h3>
                  <p className="text-sm text-green-700">
                    We found a recent verification on file. Continue to review
                    the representation details and accept the invite.
                  </p>
                </div>
              )}

              <button
                onClick={startVerificationFlow}
                disabled={buttonDisabled}
                className={`w-full py-4 px-6 rounded-lg transition-colors font-medium text-[16px] flex items-center justify-center gap-2 ${
                  buttonDisabled
                    ? "bg-blue-400 text-white cursor-not-allowed"
                    : "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
                }`}
              >
                {(loading || fetchingMandate) && (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                )}
                {requiresFreshVerification
                  ? loading
                    ? "Starting verification..."
                    : fetchingMandate
                    ? "Loading..."
                    : "Start Verification"
                  : "Continue"}
              </button>

              {/* Show reason if button is disabled */}
              {buttonDisabled && !loading && !fetchingMandate && (
                <p className="text-sm text-amber-600 mt-2 text-center">
                  {!mandateToken
                    ? "Invalid invitation link. Please reopen from your email."
                    : !tsCoachId
                    ? "Loading profile information..."
                    : "Please wait..."}
                </p>
              )}

              <p className="text-center text-sm text-gray-500 mt-6">
                This verification is secure and your data is protected according
                to our Privacy Policy
              </p>
            </div>
          </div>
      </div>
    </div>
  );
};

export default StaffMandateKycStart;
